import TalaeraBackground from "components/TalaeraBackground";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { UserContext } from "contexts/UserContext";
import { useRequiredContext } from "contexts/utils";
import { routes } from "pages/routes";
import { SignInLocationState } from "pages/SignIn/SignIn";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { logError } from "services/logging";
import { verifyUser } from "services/users/auth";
import { HTTP_CODES } from "services/restUtil";

/*
 * Verifies a pending user from the token in the URL.
 * On success it refreshes the current user so routing can continue.
 * On failure it redirects to sign-in with a localized error message.
 */
export default function VerifyPendingUser() {
  const navigate = useNavigate();
  const { refreshCurrentUser } = useRequiredContext(UserContext);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { t } = useTranslation();

  const verifyUserToken = useCallback(async () => {
    if (!token) {
      navigate(routes.public.signIn.path(), {
        state: {
          error: t("Signup.VerifyPendingUser.invalidLink"),
        } satisfies SignInLocationState,
      });
      return;
    }

    try {
      await verifyUser({ token });
    } catch (error: any) {
      logError("Failed to verify user", error);

      const errorMsgKey =
        error?.response?.status === HTTP_CODES.NOT_FOUND
          ? "Signup.VerifyPendingUser.alreadyUsedLink"
          : "Signup.VerifyPendingUser.invalidLink";
      navigate(routes.public.signIn.path(), {
        state: {
          error: t(errorMsgKey),
        } satisfies SignInLocationState,
      });
      return;
    }

    try {
      await refreshCurrentUser();
      // user will be redirected via routing
    } catch (error: any) {
      logError("Failed to load user", error);
      navigate(routes.public.signIn.path(), {
        state: {
          error: t("Signup.VerifyPendingUser.failedToSignIn"),
        } satisfies SignInLocationState,
      });
    }
  }, [navigate, token, t, refreshCurrentUser]);

  useEffect(() => {
    verifyUserToken();
  }, [verifyUserToken]);

  return (
    <TalaeraBackground>
      <TalaeraSpinner />
    </TalaeraBackground>
  );
}
